/**
 * Created by rickborst on 01/03/2017.
 */
import _ from 'lodash';

import {getInstance as getStore} from 'store/store';
import {updateUser, clearUser} from 'store/actions/user';
import {updatePaymentDetails, clearPaymentDetails} from 'store/actions/paymentdetails';
import {updateSession, setLoggedInStatus, LoggedInStatuses} from 'store/actions/session';

import {
    convertForAuthenticationWithRefreshToken,
    convertFromAuthenticationWithRefreshToken,
    convertForPersistentPayload
} from '../adapters/session';

import {
    convertFromAuthenticationWithCredentials,
    convertForAuthenticationWithCredentials,
    convertForLogout,
    convertForResetPassword,
    convertForSetPasswordWithToken,
    convertForAnonymousLogin
} from '../adapters/authentication';

import {isValidResponse} from '../validators/authentication';
import {DeviceStates, setDeviceState} from 'store/actions/widgets/player';
import { getSubscriptionPaymentMethod } from '../../../utils/firebase/remoteConfig';

import User from 'models/user';
import Session from 'models/session';
import {PaymentStatus, setStatus as setPaymentStatus} from 'store/actions/payment';


const APPLE_PAYMENT_METHOD = 'APPLE';

/**
 * Attempts an authentication with credentials.
 *
 * @param {Object} params - Credentials.
 * @param {bool} skipPaymentDetails - If set to true, do not fetch payment
 * details (for registration purpose where we do not have payment for sure).
 * @returns {Promise} Authentication promise.
 */
function authenticateWithCredentials (params, skipPaymentDetails) {
    let dataSources = this._dataSources,
        url = this._baseUrl + this._endPoints.authenticateWithCredentials,
        data = convertForAuthenticationWithCredentials.call(this, params),
        promiseChain;

    promiseChain = this.read(
        {
            url: url,
            data: data
        })
        .then(response => {

            if (!isValidResponse(response)) {

                return Promise.reject(response);
            }

            return response;
        })
        .then(response => {
            let persistentPayload = this._getPersistentPayload(),
                info = convertFromAuthenticationWithCredentials.call(this, response),
                user = new User(info.user),
                session = new Session(info.session),
                store = getStore();

            persistentPayload = Object.assign(
                persistentPayload,
                convertForPersistentPayload.call(this, session)
            );

            this._updatePersistentPayload(persistentPayload);

            dataSources.updateSession(session);

            store.dispatch(setDeviceState(DeviceStates.UNVERIFIED));
            store.dispatch(updateUser(user));
            store.dispatch(updateSession(session));
            store.dispatch(setLoggedInStatus(LoggedInStatuses.AUTHORIZED));
        });

        if (!skipPaymentDetails) {
            promiseChain = promiseChain.then(() => {
                return dataSources.getPaymentDetails()
                    .catch(() => {
                        let store = getStore();

                        store.dispatch(setPaymentStatus(PaymentStatus.REQUEST_FAILURE));
                    });
            })
            .then((paymentDetails) => {
                if (paymentDetails) {
                    return getSubscriptionPaymentMethod().then((subscriptionPaymentMethods)=> {
                        let isNeedHideCardSection = false;
                        const parsedSubscriptionPaymentMethods = JSON.parse(subscriptionPaymentMethods._value);
                        
                        Object.keys(parsedSubscriptionPaymentMethods).forEach((paymentMethod) => {
                            if (paymentMethod === APPLE_PAYMENT_METHOD && parsedSubscriptionPaymentMethods[paymentMethod].includes(Number(paymentDetails.subscriptionId))) {
                                isNeedHideCardSection = true;
                            }
                        });
        
                        paymentDetails.isNeedHideCardSection = isNeedHideCardSection;
                        
                        return paymentDetails;
                    });
                } 
                    return false;
                
            })
    
            .then(paymentDetails => {
                let store = getStore();

                if (paymentDetails) {

                    store.dispatch(updatePaymentDetails(paymentDetails));
                } else {

                    store.dispatch(clearPaymentDetails());
                }

                return Promise.resolve();
            });
        }

        return promiseChain;
}

/**
 * Attempts an authentication with credentials.
 *
 * @param {Object} params - Credentials.
 * @returns {Promise} Authentication promise.
 */
function checkUserExistance (params) {
    let url = this._baseUrl + this._endPoints.authenticateWithCredentials,
        creds = {
            username: params.username,
            password: 'null'
        },
        data = convertForAuthenticationWithCredentials.call(this, creds),
        promiseChain;

    promiseChain = this.read(
        {
            url: url,
            data: data
        })
        .then(response => {

            if (!isValidResponse(response)) {

                return Promise.reject(response);
            }

            return response;
        });

        return promiseChain;
}

/**
 * Attempts an authentication with a refresh token.
 *
 * @returns {Promise} Authentication promise.
 */
function authenticateWithRefreshToken () {
    let dataSources = this._dataSources;

    return dataSources.getSession()
        .then(session => convertForAuthenticationWithRefreshToken.call(this, session))
        .then(data => {
            let url = this._baseUrl + this._endPoints.authenticateWithRefreshToken;

            if (data.ks) {
                return this.read({
                    url: url,
                    data: data
                });
            }
            return Promise.reject(data);
        })
        .then(response => {
            if (!isValidResponse(response)) {
                return Promise.reject(response);
            }

            return response;
        })
        .then(response => {
            let data = convertFromAuthenticationWithRefreshToken.call(this, response);

            return dataSources.updateSession(new Session(data));
        })
        .then(session => {
            let persistentPayload = this._getPersistentPayload();

            persistentPayload = Object.assign(
                persistentPayload,
                convertForPersistentPayload.call(this, session)
            );

            this._updatePersistentPayload(persistentPayload);

            return Promise.all([
                session,
                dataSources.getActiveUser()
            ]);
        })
        .then(results => {
            let session = results[0],
                user = results[1],
                store = getStore();

            store.dispatch(setDeviceState(DeviceStates.UNVERIFIED));
            store.dispatch(updateUser(user));
            store.dispatch(updateSession(session));
            store.dispatch(setLoggedInStatus(LoggedInStatuses.AUTHORIZED));
        })
        .then(() => {
            return dataSources.getPaymentDetails.bind(this)()
                .catch(() => {
                    let store = getStore(),
                        state = store.getState(),
                        paymentStatus = _.get(state, ['payment', 'status']);

                    if (paymentStatus !== PaymentStatus.PROCESSING_PAYMENT) {
                        store.dispatch(setPaymentStatus(PaymentStatus.REQUEST_FAILURE));
                    }
                });
        })

        .then((paymentDetails) => {
            if (paymentDetails) {
                return getSubscriptionPaymentMethod().then((subscriptionPaymentMethods)=> {
                    let isNeedHideCardSection = false;
                    const parsedSubscriptionPaymentMethods = JSON.parse(subscriptionPaymentMethods._value);
                    
                    Object.keys(parsedSubscriptionPaymentMethods).forEach((paymentMethod) => {
                        if (paymentMethod === APPLE_PAYMENT_METHOD && parsedSubscriptionPaymentMethods[paymentMethod].includes(Number(paymentDetails.subscriptionId))) {
                            isNeedHideCardSection = true;
                        }
                    });
    
                    paymentDetails.isNeedHideCardSection = isNeedHideCardSection;
    
                    return paymentDetails;
                });
            } 
                return false;
            
        })

        .then(paymentDetails => {
            let store = getStore();

            if (paymentDetails) {
                
                store.dispatch(updatePaymentDetails(paymentDetails));
            } else {

                store.dispatch(clearPaymentDetails());
            }

            return Promise.resolve();
        });
}

/**
 * Requests tokens for anonymous access.
 *
 * @returns {Promise} Authentication promise.
 */
function anonymousLogin () {

    let url = this._baseUrl + this._endPoints.anonymousLogin,
        data = convertForAnonymousLogin.call(this),
        dataSources = this._dataSources;

    return this.create({
        url: url,
        data: data
    })
    .then(response => {

        if (!isValidResponse(response)) {
            return Promise.reject(response);
        }

        return response;
    })
    .then(response => {
        let sessionData = convertFromAuthenticationWithRefreshToken.call(this, response);

        return dataSources.updateSession(new Session(sessionData));
    })
    .then(session => {
        let persistentPayload = this._getPersistentPayload();

        persistentPayload = Object.assign(
            persistentPayload,
            convertForPersistentPayload.call(this, session)
        );

        this._updatePersistentPayload(persistentPayload);

        return session;
    })
    .then(session => {
        let store = getStore();

        store.dispatch(updateSession(session));
        store.dispatch(setLoggedInStatus(LoggedInStatuses.UNAUTHORIZED));

        return Promise.resolve();
    });
}

/**
 * Logs the current user out.
 *
 * @returns {Promise} Logout promise.
 */
function logout () {

    return this._dataSources.getSession()
        .then(session => convertForLogout.call(this, session))
        .then(data => {
            const url = this._baseUrl + this._endPoints.logout;

            return this.create({
                url: url,
                data: data
            });
        })
        .then(response => {
            const store = getStore();

            store.dispatch(clearUser());
            store.dispatch(updateSession(null));
            store.dispatch(setLoggedInStatus(LoggedInStatuses.UNAUTHORIZED));

            return Promise.resolve(response.result);
        });
}

/**
 * Ask for a reset password link.
 *
 * @param {string} email - E-mail of the user.
 * @returns {Promise} Reset password promise response.
 */
function resetPassword (email) {
    let url = this._baseUrl + this._endPoints.resetPassword,
        data = convertForResetPassword.call(this, email);

    return this.create({
        url: url,
        data: data
    })
        .then(response => {

            if (!isValidResponse(response) || response.result?.error?.code === '1011') {
                return Promise.reject(response);
            }

            return response;
        });
}

/**
 * Sets a new password for the user, using a
 * token received through forgot password email.
 *
 * @param {string} token - Token received per email.
 * @param {string} password - New password entered by user.
 * @returns {Promise} Set password promise.
 */
function setPasswordWithToken (token, password) {
    let url = this._baseUrl + this._endPoints.setPasswordWithToken,
        data = convertForSetPasswordWithToken.call(this, token, password);

    return this.update({
        url: url,
        data: data
    })
        .then(response => {

            if (!isValidResponse(response)) {
                return Promise.reject(response);
            }

            return response;
        });
}

export {
    authenticateWithCredentials,
    checkUserExistance,
    authenticateWithRefreshToken,
    logout,
    resetPassword,
    setPasswordWithToken,
    anonymousLogin
};


// WEBPACK FOOTER //
// ./src/js/datasources/kaltura/services/authentication.js